import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALCATEGORYS_LOAD,
    ACTION_TRIALCATEGORYS_LOAD_BY_ID,
    ACTION_TRIALCATEGORYS_LOAD_FAILURE,
    ACTION_TRIALCATEGORYS_LOAD_SUCCESS,

    ACTION_TRIALCATEGORYS_SAVE,
    ACTION_TRIALCATEGORYS_SAVE_SUCCESS,
    ACTION_TRIALCATEGORYS_SAVE_FAILURE,

    ACTION_TRIALCATEGORYS_CREATE,
    ACTION_TRIALCATEGORYS_CREATE_SUCCESS,
    ACTION_TRIALCATEGORYS_CREATE_FAILURE,

    ACTION_TRIALCATEGORYS_CLEAR,
    ACTION_TRIALCATEGORYS_FILTER,

    TrialCategoryAction
} from '../actions/trialCategorys';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";


export interface ITrialCategoryState {
    formData: Dtos.TrialCategory | Dtos.TrialCategory[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    formRoles: Dtos.RoleType[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialCategory[];
}

const initialState: ITrialCategoryState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    formRoles: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: []
};


function loading(state: ITrialCategoryState, isLoading: boolean): ITrialCategoryState {
    const newState: ITrialCategoryState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: ITrialCategoryState,
    formData: Dtos.TrialCategory | Dtos.TrialCategory[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialCategoryState {
    const newState: ITrialCategoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: ITrialCategoryState, formFilter: IFormFilter): ITrialCategoryState {

    const trialcategorys: Dtos.TrialCategory[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = trialcategorys ? trialcategorys.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: ITrialCategoryState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        }
    });

    return newState;
}

function loadFailure(state: ITrialCategoryState, responseStatus: Dtos.ResponseStatus): ITrialCategoryState {
    const newState: ITrialCategoryState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: ITrialCategoryState, propertiesToClear?: string[]): ITrialCategoryState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: ITrialCategoryState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialCategoryState, isSaving: boolean): ITrialCategoryState {
    const newState: ITrialCategoryState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialCategoryState,
    formData: Dtos.TrialCategory | Dtos.TrialCategory[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialCategoryState {
    const newState: ITrialCategoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialCategoryState, responseStatus: Dtos.ResponseStatus): ITrialCategoryState {
    const newState: ITrialCategoryState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialCategoryState, isCreating: boolean): ITrialCategoryState {
    const newState: ITrialCategoryState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialCategoryState,
    formData: Dtos.TrialCategory,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[]
): ITrialCategoryState {
    const newState: ITrialCategoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialCategoryState, responseStatus: Dtos.ResponseStatus): ITrialCategoryState {
    const newState: ITrialCategoryState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialCategoryReducer: Reducer<ITrialCategoryState> = (state: ITrialCategoryState = initialState, action: TrialCategoryAction) => {
    switch (action.type) {
        case ACTION_TRIALCATEGORYS_LOAD:
        case ACTION_TRIALCATEGORYS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALCATEGORYS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups, action.responseStatus);
       case ACTION_TRIALCATEGORYS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALCATEGORYS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALCATEGORYS_SAVE:
            return saving(state, true);
        case ACTION_TRIALCATEGORYS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALCATEGORYS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALCATEGORYS_CREATE:
            return creating(state, true);
        case ACTION_TRIALCATEGORYS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups);
        case ACTION_TRIALCATEGORYS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALCATEGORYS_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default TrialCategoryReducer;